import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { RequerimentoService } from '../../common/services/requerimento';

export const getRequerimentosStatus = createAsyncThunk(
  'requerimentos/getRequerimentosStatus',
  async ({ status }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { data } = state.user;
      const { matricula, token } = data || {};

      if (!matricula || !token) {
        return rejectWithValue('Usuário não autenticado.');
      }

      const service = new RequerimentoService();
      const response = await service.obterStatusRequerimento({
        matricula,
        token,
        status,
      });

      if (!response.data) {
        return { status, data: [] };
      }

      const arrayAnexosPendentesPromises = response.data.map(
        async (requerimento) => {
          try {
            const request = await service.obterAnexosPendentesPorProtocolo({
              protocolo: requerimento.Protocolo,
              matricula,
              token,
            });

            return { ...requerimento, anexosPendentes: request.data };
          } catch {
            return requerimento;
          }
        }
      );

      const arrayRequerimentosComAnexosPendentes = await Promise.all(
        arrayAnexosPendentesPromises
      );

      return { status, data: arrayRequerimentosComAnexosPendentes };
    } catch (error) {
      return rejectWithValue('Erro inesperado. Tente novamente mais tarde.');
    }
  }
);

export const getRequerimentosPeritos = createAsyncThunk(
  'requerimentos/getRequerimentosPeritos',
  async (_, { rejectWithValue }) => {
    try {
      const service = new RequerimentoService();
      const medicosPeritos = await service.obterMedicosPeritos();
      return medicosPeritos;
    } catch (error) {
      return rejectWithValue('Erro inesperado. Tente novamente mais tarde.');
    }
  }
);

export const getRequerimentosForms = createAsyncThunk(
  'requerimentos/getRequerimentosForms',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { data, dadosPessoais } = state.user || {};
      const { matricula, token, plano } = data || {};

      const planosPrevidencia = dadosPessoais?.planoPrevidencia || [];

      if (!matricula || !token || !plano || planosPrevidencia.length === 0) {
        return rejectWithValue(
          'Dados do usuário inválidos para requerimentos.'
        );
      }

      const planoAtivo =
        planosPrevidencia.find(
          (planItem) => String(planItem.id) === String(plano)
        ) || planosPrevidencia[0];

      const sitpart = planoAtivo?.sitPart ?? '';
      const tipoBeneficio = planoAtivo?.tipoBeneficio ?? '';

      const service = new RequerimentoService();

      const forms = await service.obterFormulariosDisponiveis({
        matricula,
        token,
        plano,
        sitpart,
        beneficio: tipoBeneficio,
      });

      const formLabels = await service.obterTitulosFormularios();
      const fomatedFormLabels = formLabels.results.map((form) => ({
        referencia: form.Referencia,
        nome: form.Title,
      }));

      return { forms, formLabels: fomatedFormLabels };
    } catch (error) {
      return rejectWithValue('Erro inesperado. Tente novamente mais tarde.');
    }
  }
);

const separarCamposAnexos = (campos) => {
  const camposSemAnexo = Object.keys(campos)
    .filter((key) => campos[key] !== '' && !key.includes('anexo'))
    .reduce((acc, key) => {
      return { ...acc, [key]: campos[key] };
    }, {});

  const camposAnexosPrenchidos = Object.keys(campos)
    .filter((key) => key.includes('anexo') && !!campos[key])
    .reduce((acc, key) => {
      return [
        ...acc,
        {
          name: key,
          file: campos[key],
        },
      ];
    }, []);

  return { camposSemAnexo, camposAnexosPrenchidos };
};

const mapearDadosCadastrais = (dadosAtualizados, dadosSAP) => ({
  email: dadosAtualizados?.email,
  celular: dadosAtualizados?.celular,
  telefoneFixo: dadosAtualizados?.telefoneFixo,
  nome: dadosSAP?.nome,
  dtNascimento: dadosAtualizados?.dtNascimento,
  nacionalidade: dadosAtualizados?.nacionalidade,
  sexo: dadosAtualizados?.sexo,
  estadoCivil: dadosAtualizados?.estadoCivil,
  pai: dadosAtualizados?.pai,
  mae: dadosAtualizados?.mae,
  rg: dadosAtualizados?.rg,
  orgEmissor: dadosAtualizados?.orgEmissor,
  uf: dadosAtualizados?.uf,
  cpf: dadosSAP?.cpf,
  dtEmissao: dadosAtualizados?.dtEmissao,
  endereco: dadosAtualizados?.endereco,
  numero: dadosAtualizados?.numero,
  complemento: dadosAtualizados?.complemento,
  cep: dadosAtualizados?.cep,
  bairro: dadosAtualizados?.bairro,
  cidade: dadosAtualizados?.cidade,
  estado: dadosAtualizados?.estado,
  banco: dadosAtualizados?.banco,
  agencia: dadosAtualizados?.agencia,
  conta: dadosAtualizados?.conta,
  digito: dadosAtualizados?.digito,
});

const enviarAnexos = async ({
  anexos,
  service,
  matricula,
  token,
  protocolo,
  isEmprestimo = false,
}) => {
  if (!Array.isArray(anexos) || anexos.length === 0) return;

  if (isEmprestimo) {
    // eslint-disable-next-line no-restricted-syntax
    for (const anexo of anexos) {
      // eslint-disable-next-line no-await-in-loop
      await service.persistirAnexo({
        anexo,
        protocolo,
        matricula,
        token,
        nomearquivo: anexo.name,
        tipoanexo: 'Emprestimo',
      });
    }
    return;
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const anexo of anexos) {
    const arquivos = Array.isArray(anexo.file)
      ? anexo.file
      : Array.from(anexo.file || []);

    // eslint-disable-next-line no-restricted-syntax
    for (const arquivo of arquivos) {
      // eslint-disable-next-line no-await-in-loop
      await service.persistirAnexo({
        anexo: arquivo,
        protocolo,
        matricula,
        token,
        nomearquivo: arquivo.name,
        tipoanexo: anexo.name.replace('anexo', ''),
      });
    }
  }
};

export const enviarAnexoComplementar = createAsyncThunk(
  'requerimentos/enviarAnexoComplementar',
  async ({ anexo, protocolo }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { data } = state.user || {};
      const { matricula, token } = data || {};

      if (!matricula || !token) {
        return rejectWithValue('Usuário não autenticado.');
      }

      const { camposAnexosPrenchidos } = separarCamposAnexos(anexo);

      if (camposAnexosPrenchidos.length === 0) {
        return rejectWithValue('Nenhum anexo para enviar.');
      }

      const service = new RequerimentoService();
      await enviarAnexos({
        anexos: camposAnexosPrenchidos,
        service,
        matricula,
        token,
        protocolo,
      });

      toast.success('Anexo(s) enviados com sucesso.');
      return { success: true };
    } catch (error) {
      return rejectWithValue('Erro ao enviar anexo(s).');
    }
  }
);

export const enviarRequerimentoFormulario = createAsyncThunk(
  'requerimentos/enviarRequerimentoFormulario',
  async (
    { campos, formulario, salvar = false },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const { data } = state.user || {};
      const { matricula, token } = data || {};

      if (!matricula || !token) {
        return rejectWithValue('Usuário não autenticado.');
      }

      const service = new RequerimentoService();

      const protocoloResponse = await service.gerarProtocoloAtendimento(
        matricula,
        token
      );

      const protocolo =
        protocoloResponse?.data?.[0]?.protocolo ??
        protocoloResponse?.data?.[0]?.Protocolo ??
        '';

      if (!protocolo) {
        return rejectWithValue('Falha ao gerar protocolo.');
      }

      const { camposSemAnexo, camposAnexosPrenchidos } =
        separarCamposAnexos(campos);

      const responseForm = await service.persistirFormulario(
        matricula,
        token,
        camposSemAnexo,
        formulario,
        protocolo
      );

      if (
        responseForm.status !== 200 ||
        !responseForm.data ||
        !responseForm.data[0] ||
        !responseForm.data[0].linhasafetadas
      ) {
        return rejectWithValue('Erro ao enviar requerimento.');
      }

      await enviarAnexos({
        anexos: camposAnexosPrenchidos,
        service,
        matricula,
        token,
        protocolo,
      });

      const { dadosSAPNaoTratados } = state.user || {};
      const { dadosPessoaisRequerimento: dadosAtualizados } =
        state.requerimentos || {};

      const dadoscadastrais = mapearDadosCadastrais(
        dadosAtualizados,
        dadosSAPNaoTratados
      );

      await service.concluirRequerimento({
        token,
        matricula,
        formulario,
        protocolo,
        dadoscadastrais,
      });

      return {
        protocolo,
        formulario,
        salvar,
      };
    } catch (error) {
      return rejectWithValue(
        'Erro ao enviar requerimento. Favor re-logar e tentar novamente.'
      );
    }
  }
);

/** Lista para API sem anexos (serializável). */
const listaSemAnexos = (lista) =>
  (lista || []).map((item) => {
    const { anexos, ...rest } = item;
    return rest;
  });

export const criarRascunhoRequerimento = createAsyncThunk(
  'requerimentos/criarRascunhoRequerimento',
  async (formulario, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { data } = state.user || {};
      const { matricula, token } = data || {};

      if (!matricula || !token) {
        return rejectWithValue('Usuário não autenticado.');
      }

      const service = new RequerimentoService();
      const protocoloResponse = await service.gerarProtocoloAtendimento(
        matricula,
        token
      );

      const protocolo =
        protocoloResponse?.data?.[0]?.protocolo ??
        protocoloResponse?.data?.[0]?.Protocolo ??
        '';

      if (!protocolo) {
        return rejectWithValue('Falha ao gerar protocolo.');
      }

      const responseForm = await service.persistirFormulario(
        matricula,
        token,
        [],
        formulario,
        protocolo
      );

      if (
        responseForm.status !== 200 ||
        !responseForm.data?.[0]?.linhasafetadas
      ) {
        return rejectWithValue('Erro ao criar rascunho.');
      }

      return { protocolo, formularioLista: [] };
    } catch (error) {
      return rejectWithValue('Erro ao criar rascunho.');
    }
  }
);

export const adicionarItemRascunhoRequerimento = createAsyncThunk(
  'requerimentos/adicionarItemRascunhoRequerimento',
  async ({ campos, successMessage }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { data } = state.user || {};
      const { matricula, token } = data || {};
      const { protocolo, formularioLista } = state.requerimentos || {};

      if (!matricula || !token || !protocolo) {
        return rejectWithValue('Dados do rascunho inválidos.');
      }

      const { camposSemAnexo, camposAnexosPrenchidos } =
        separarCamposAnexos(campos);

      const listaAntiga = Array.isArray(formularioLista) ? formularioLista : [];
      const listaParaApi = [...listaSemAnexos(listaAntiga), camposSemAnexo];
      const novaListaComAnexos = [
        ...listaAntiga,
        { ...camposSemAnexo, anexos: camposAnexosPrenchidos },
      ];

      const service = new RequerimentoService();
      const responseForm = await service.atualizaRequerimento({
        matricula,
        token,
        campos: listaParaApi,
        protocolo,
      });

      if (
        responseForm.status !== 200 ||
        !responseForm.data?.[0]?.linhasafetadas
      ) {
        toast.error('Oops... Algo deu errado');
        return rejectWithValue('Erro ao adicionar item.');
      }

      if (successMessage) {
        toast.success(successMessage);
      }
      return { formularioLista: novaListaComAnexos };
    } catch (error) {
      toast.error('Oops... Algo deu errado');
      return rejectWithValue('Erro ao adicionar item.');
    }
  }
);

export const atualizarItemRascunhoRequerimento = createAsyncThunk(
  'requerimentos/atualizarItemRascunhoRequerimento',
  async (
    { campos, camposAntigos, successMessage, chaveAtualizacao = 'cpf' },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const { data } = state.user || {};
      const { matricula, token } = data || {};
      const { protocolo, formularioLista } = state.requerimentos || {};

      if (!matricula || !token || !protocolo) {
        return rejectWithValue('Dados do rascunho inválidos.');
      }

      const { camposSemAnexo, camposAnexosPrenchidos } =
        separarCamposAnexos(campos);

      const listaAntiga = Array.isArray(formularioLista) ? formularioLista : [];
      const valorChave =
        camposAntigos?.[chaveAtualizacao] != null
          ? camposAntigos[chaveAtualizacao]
          : null;

      const newListWithItemUpdated = listaAntiga.map((item) => {
        if (valorChave != null && item[chaveAtualizacao] === valorChave) {
          return { ...camposSemAnexo, anexos: camposAnexosPrenchidos };
        }
        return item;
      });

      const listaParaApi = listaSemAnexos(newListWithItemUpdated);

      const service = new RequerimentoService();
      const responseForm = await service.atualizaRequerimento({
        matricula,
        token,
        campos: listaParaApi,
        protocolo,
      });

      if (
        responseForm.status !== 200 ||
        !responseForm.data?.[0]?.linhasafetadas
      ) {
        toast.error('Oops... Algo deu errado');
        return rejectWithValue('Erro ao atualizar item.');
      }

      if (successMessage) {
        toast.success(successMessage);
      }
      return { formularioLista: newListWithItemUpdated };
    } catch (error) {
      toast.error('Oops... Algo deu errado');
      return rejectWithValue('Erro ao atualizar item.');
    }
  }
);

export const excluirItemRascunhoRequerimento = createAsyncThunk(
  'requerimentos/excluirItemRascunhoRequerimento',
  async ({ campos, chaveExclusao = 'cpf' }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { data } = state.user || {};
      const { matricula, token } = data || {};
      const { protocolo, formularioLista } = state.requerimentos || {};

      if (!matricula || !token || !protocolo) {
        return rejectWithValue('Dados do rascunho inválidos.');
      }

      const listaAntiga = Array.isArray(formularioLista) ? formularioLista : [];
      const valorChave = campos?.[chaveExclusao];
      const newListWithItemExcluded =
        valorChave != null
          ? listaAntiga.filter((item) => item[chaveExclusao] !== valorChave)
          : listaAntiga;

      const listaParaApi = listaSemAnexos(newListWithItemExcluded);

      const service = new RequerimentoService();
      const responseForm = await service.atualizaRequerimento({
        matricula,
        token,
        campos: listaParaApi,
        protocolo,
      });

      if (
        responseForm.status !== 200 ||
        !responseForm.data?.[0]?.linhasafetadas
      ) {
        return rejectWithValue('Erro ao excluir item.');
      }

      return { formularioLista: newListWithItemExcluded };
    } catch (error) {
      return rejectWithValue('Erro ao excluir item.');
    }
  }
);

export const concluirEnvioRequerimento = createAsyncThunk(
  'requerimentos/concluirEnvioRequerimento',
  async ({ formulario }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { data, dadosSAPNaoTratados } = state.user || {};
      const { matricula, token } = data || {};
      const {
        protocolo,
        formularioLista,
        dadosPessoaisRequerimento: dadosAtualizados,
      } = state.requerimentos || {};

      if (!matricula || !token || !protocolo) {
        return rejectWithValue('Dados do requerimento inválidos.');
      }

      const dadoscadastrais = mapearDadosCadastrais(
        dadosAtualizados,
        dadosSAPNaoTratados
      );

      const allAttachments = (formularioLista || []).reduce(
        (prev, curr) => [...prev, ...(curr.anexos || [])],
        []
      );

      const service = new RequerimentoService();
      if (allAttachments.length > 0) {
        await enviarAnexos({
          anexos: allAttachments,
          service,
          matricula,
          token,
          protocolo,
        });
      }

      await service.concluirRequerimento({
        token,
        matricula,
        formulario,
        protocolo,
        dadoscadastrais,
      });

      return { success: true, formularioLista: [] };
    } catch (error) {
      return rejectWithValue('Erro ao concluir requerimento. Tente novamente.');
    }
  }
);
